<template>
  <div class="card">
    <div class="card-header">
      <div class="card-header-title">
        <div class="title is-6 is-marginless">Week's Deal</div>
      </div>
    </div>

    <form @submit.prevent="submit">
      <div class="card-content">
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">Ενεργό</label>

              <div class="control">
                <ToggleButton
                  :sync="true"
                  v-model="form.active"
                  :disabled="isSaving || forceOff"
                  :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                  :width="30"
                  :height="15"
                  @change="onToggleChange"
                />
              </div>

              <p v-if="isExpired" class="help is-danger">
                Η προσφορά έχει λήξει και δεν μπορεί να ενεργοποιηθεί.
              </p>
              <p v-if="stockLimited" class="help is-danger">
                Η ενεργοποίηση δεν επιτρέπεται. Το προϊόν δεν είναι μόνιμα
                διαθέσιμο και διαθέτει έως 1 τεμ. σε απόθεμα.
              </p>
            </div>
          </div>

          <div class="column">
            <label class="label">Ημερομηνία Λήξης *</label>
            <div class="field has-addons">
              <div class="control is-expanded">
                <Datepicker
                  v-validate="'required'"
                  v-model="form.expiration_date"
                  name="expiration_date"
                  format="dd-MM-yyyy"
                  language="el"
                  wrapper-class="field"
                  input-class="input"
                  placeholder="πχ: 02-12-2017"
                />
                <p v-if="errors.has('expiration_date')" class="help is-danger">
                  Εισάγετε ημερομηνία λήξης
                </p>
              </div>
              <div v-if="form.expiration_date" class="control">
                <button class="button" type="button" @click="clearDate">
                  <span class="icon"><i class="fa fa-times"/></span>
                </button>
              </div>
            </div>

            <p class="expiry-note">
              <span class="icon is-small">
                <i class="fa fa-exclamation-triangle" />
              </span>
              Η επιλεγμένη ημερομηνία λήξης είναι η ημέρα που η προωθητική
              ενέργεια παύει να εμφανίζεται. Π.χ. αν επιλέξετε 5 Σεπ 2025, η
              προσφορά δεν θα εμφανίζεται από την αρχή της ημέρας 5 Σεπ 2025.
            </p>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">Προϊόν *</label>
              <products-search
                v-validate="'required'"
                v-model="form.product_id"
                data-vv-name="product_id"
                @select-product="handleSelectProduct"
              />
              <p v-if="errors.has('product_id')" class="help is-danger">
                Επιλέξτε προϊόν
              </p>
            </div>
          </div>
        </div>

        <div v-if="hasProduct">
          <div class="deal-warning">
            <span class="icon is-small">
              <i class="fa fa-exclamation-triangle" />
            </span>
            <span>
              Εάν το προϊόν δεν είναι <strong>μόνιμα διαθέσιμο</strong> και το
              <strong>απόθεμα είναι 1 ή λιγότερο</strong>, η προσφορά δεν θα
              εμφανίζεται στο UI και το deal θα απενεργοποιείται.
            </span>
          </div>

          <table class="table is-bordered is-striped is-fullwidth">
            <thead>
              <th>Φωτό</th>
              <th>Προϊόν</th>
              <th>Απόθεμα</th>
              <th>Π.Λ. Τιμή (&euro;)</th>
              <th>Έκπτωση (%)</th>
              <th>Τελική Τιμή (&euro;)</th>
              <th>Φ.Π.Α. (%)</th>
              <th>Μόνιμα διαθέσιμο</th>
              <th class="hidden-print">Ενέργειες</th>
            </thead>
            <tbody>
              <product
                :product="newProduct"
                :is-editing="true"
                @delete-item="handleDeleteItem"
              />
            </tbody>
          </table>
        </div>
      </div>

      <div class="card-footer">
        <FormSubmit :is-saving="isSaving" @reset="reset" />
      </div>
    </form>
  </div>
</template>

<script>
import moment from 'moment';
import clone from 'lodash/clone';
import isEmpty from 'lodash/isEmpty';
import Datepicker from 'vuejs-datepicker';
import request from '@/utils/request';
import FormSubmit from '@/views/components/FormSubmit';
import Notification from '@/views/components/Notification';
import Product from './components/Product';
import ProductsSearch from './components/ProductsSearch';

export default {
  components: {
    Notification,
    Datepicker,
    ProductsSearch,
    Product,
    FormSubmit,
  },
  data() {
    return {
      form: {
        active: false,
        expiration_date: null, // Date object from Datepicker
        product_id: null,
      },
      deal: {
        active: false,
        expiration_date: null,
        product_id: null,
      },
      newProduct: {},
      product: {},
      isSaving: false,
    };
  },
  computed: {
    hasProduct() {
      return !isEmpty(this.newProduct);
    },
    // Expired from the start of the selected day (offer stops at start of that day)
    isExpired() {
      if (!this.form.expiration_date) return false;
      const exp = moment(this.form.expiration_date).startOf('day');
      const today = moment().startOf('day');
      return exp.isSameOrBefore(today);
    },
    // Rule: not available forever AND stock ≤ 1
    stockLimited() {
      if (!this.hasProduct) return false;
      const stock = Number(this.newProduct.stock || 0);
      return this.newProduct.available_forever === false && stock <= 1;
    },
    // Any blocking condition disables the toggle and forces it off
    forceOff() {
      return this.isExpired || this.stockLimited;
    },
  },
  watch: {
    // If constraints become true while on page, snap toggle off
    forceOff(newVal) {
      if (newVal && this.form.active) {
        this.form.active = false;
      }
    },
  },
  async created() {
    try {
      const response = await request.get('/weeks-deal');
      const { deal } = response.data;

      if (deal) {
        this.product = response.data.deal.product || {};
        this.newProduct = clone(this.product);

        this.deal = {
          ...response.data.deal,
          product_id: this.newProduct.id,
        };
      }

      this.form = clone(this.deal);
    } catch (err) {
      this.$router.push('/error').catch(e => console.log(e));
    }
  },
  methods: {
    async onToggleChange() {
      if (this.forceOff) {
        if (this.form.active) this.form.active = false;
        return;
      }
      try {
        await this.saveDeal();
      } catch (e) {
        this.form.active = !!(this.deal && this.deal.active);
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'Η αποθήκευση της προσφοράς απέτυχε',
        });
      }
    },

    async saveDeal(extra = {}) {
      this.isSaving = true;
      try {
        const payload = {
          ...this.form,
          ...extra,
          expiration_date: this.form.expiration_date
            ? moment(this.form.expiration_date).format('YYYY-MM-DD')
            : null,
        };

        if (this.forceOff) payload.active = false;

        await request.post('/weeks-deal', payload);

        this.deal = clone(payload);
        this.product = clone(this.newProduct);
      } finally {
        this.isSaving = false;
      }
    },

    async submit() {
      try {
        const isValid = await this.$validator.validateAll();
        if (!isValid) throw Error('Client-side validation failed');

        if (this.forceOff && this.form.active) this.form.active = false;

        await this.saveDeal();

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η προσφορά αποθηκεύτηκε',
        });
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      }
    },

    clearDate() {
      this.form.expiration_date = null;
    },

    handleSelectProduct(product) {
      this.newProduct = product;
      this.form.product_id = product.id;
    },

    handleDeleteItem() {
      this.newProduct = {};
      this.form.product_id = null;
    },

    reset() {
      this.form = clone(this.deal);
      this.newProduct = clone(this.product);
      this.$validator.reset();
    },
  },
};
</script>

<style scoped lang="scss">
.table {
  margin: 0;
}

.expiry-note {
  margin-top: 0.5rem;
  padding: 0.6rem 0.75rem;
}

.deal-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  margin: 0.75rem 0 1rem;
  font-weight: 600;
}

.fa {
  &.fa-shopping-basket {
    font-size: 40px;
    margin-bottom: 1rem;
  }
}
</style>
